import moment from 'moment';
import type { PatientV2Detail } from './types';

export const digitsOnly = (value: unknown) =>
  String(value ?? '').replace(/\D/g, '');
export const brazilianStates =
  'AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO'.split(
    ' '
  );

export const parsePatientDate = (value: string) => {
  const text = value.trim();
  if (/^\d{4}-\d{2}-\d{2}T/.test(text))
    return moment.parseZone(text, moment.ISO_8601, true);
  return moment(text, ['DD/MM/YYYY', 'DDMMYYYY', 'YYYY-MM-DD'], true);
};
export const displayPatientDate = (value: string) => {
  const parsed = parsePatientDate(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : value;
};
export const dateError = (value: unknown): string => {
  const text = String(value ?? '').trim();
  if (!text) return '';
  const parsed = parsePatientDate(text);
  if (!parsed.isValid())
    return 'Informe uma data válida no formato DD/MM/AAAA.';
  if (parsed.year() < 1) return 'Informe um ano igual ou posterior a 0001.';
  return parsed.isAfter(moment(), 'day')
    ? 'A data não pode estar no futuro.'
    : '';
};
export const databaseDate = (value: string) =>
  value.trim() ? parsePatientDate(value).format('YYYY-MM-DD') : '';

export const isValidCpf = (value: unknown) => {
  const cpf = digitsOnly(value);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

  const calculateDigit = (length: number) => {
    let sum = 0;
    for (let index = 0; index < length; index += 1) {
      sum += Number(cpf[index]) * (length + 1 - index);
    }
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };

  return calculateDigit(9) === Number(cpf[9]) && calculateDigit(10) === Number(cpf[10]);
};
export const decimalError = (value: unknown, max: number) => {
  const text = String(value ?? '')
    .trim()
    .replace(',', '.');
  if (!text) return '';
  return /^\d+(\.\d{1,2})?$/.test(text) && Number(text) <= max
    ? ''
    : `Informe um número entre 0 e ${String(max).replace(
        '.',
        ','
      )}, com até duas casas decimais.`;
};
export const crmParts = (clinical: PatientV2Detail['clinical']) => {
  const match = (clinical.requestingDoctorCrm ?? '').match(
    /^(CRM-[A-Z]{2})\s+(\d+)$/i
  );
  return {
    requestingDoctorCrmUf: clinical.requestingDoctorCrmUf ?? match?.[1] ?? '',
    requestingDoctorCrmNumber:
      clinical.requestingDoctorCrmNumber ?? match?.[2] ?? '',
  };
};
export const normalizePatientForm = (
  patient: PatientV2Detail
): PatientV2Detail => {
  const parts = crmParts(patient.clinical);
  return {
    ...patient,
    cpf: digitsOnly(patient.cpf),
    email: patient.email.trim(),
    uf: patient.uf.trim().toUpperCase(),
    birthDate: databaseDate(patient.birthDate),
    clinical: {
      ...patient.clinical,
      ...parts,
      requestDate: databaseDate(patient.clinical.requestDate),
      oxygenFlow: String(patient.clinical.oxygenFlow ?? '')
        .trim()
        .replace(',', '.'),
      dailyUse: String(patient.clinical.dailyUse ?? '')
        .trim()
        .replace(',', '.'),
      requestingDoctorCrm:
        parts.requestingDoctorCrmUf || parts.requestingDoctorCrmNumber
          ? `${parts.requestingDoctorCrmUf} ${parts.requestingDoctorCrmNumber}`.trim()
          : patient.clinical.requestingDoctorCrmUf !== undefined ||
            patient.clinical.requestingDoctorCrmNumber !== undefined
          ? ''
          : patient.clinical.requestingDoctorCrm,
    },
  };
};
